import { Button } from "@/components/ui/button";
import { Pencil, Trash2, Layout } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { TablePagination } from "../ui/data-table/TablePagination";
import { Switch } from "../ui/switch";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

interface BusinessType {
  id: string;
  name: string;
  description: string | null;
  features: {
    stock?: boolean;
    budget?: boolean;
    history?: boolean;
    returns?: boolean;
    counting?: boolean;
    settings?: boolean;
    poultry?: boolean;
  };
}

interface BusinessTypeTableProps {
  businessTypes: BusinessType[];
  onEdit: (type: BusinessType) => void;
  onDelete: (id: string) => void;
}

export const BusinessTypeTable = ({
  businessTypes,
  onEdit,
  onDelete,
}: BusinessTypeTableProps) => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const totalPages = Math.ceil(businessTypes.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentItems = businessTypes.slice(startIndex, endIndex);

  const featureLabels = {
    stock: "Stock",
    budget: "Budget",
    history: "Historique",
    returns: "Retours",
    counting: "Comptage",
    settings: "Paramètres",
    poultry: "Ferme avicole",
  };

  const handleFeatureToggle = async (businessTypeId: string, feature: string, currentValue: boolean) => {
    try {
      const { data, error } = await supabase
        .from('business_types')
        .update({
          features: {
            ...businessTypes.find(type => type.id === businessTypeId)?.features,
            [feature]: !currentValue
          }
        })
        .eq('id', businessTypeId)
        .select()
        .single();

      if (error) throw error;
      
      toast.success(`Fonctionnalité ${featureLabels[feature as keyof typeof featureLabels]} ${!currentValue ? 'activée' : 'désactivée'}`);
    } catch (error) {
      console.error('Erreur lors de la mise à jour:', error);
      toast.error("Erreur lors de la mise à jour des fonctionnalités");
    }
  };

  return (
    <div className="space-y-4">
      <div className="border rounded-lg">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Nom
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Description
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Fonctionnalités
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {currentItems?.map((type) => (
              <tr key={type.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {type.name}
                </td>
                <td className="px-6 py-4 text-sm text-gray-500">
                  {type.description}
                </td>
                <td className="px-6 py-4">
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(featureLabels).map(([key, label]) => (
                      <div key={key} className="flex items-center space-x-2">
                        <Switch
                          checked={type.features?.[key as keyof typeof type.features] ?? false}
                          onCheckedChange={() => handleFeatureToggle(
                            type.id,
                            key,
                            type.features?.[key as keyof typeof type.features] ?? false
                          )}
                        />
                        <span className="text-sm text-gray-600">{label}</span>
                      </div>
                    ))}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div className="flex justify-end space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onEdit(type)}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => onDelete(type.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/admin/business-types/${type.id}/interface`)}
                    >
                      <Layout className="w-4 h-4" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <TablePagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
        />
      )}
    </div>
  );
};